"use client";
import { useSpring, animated, useInView, useSprings } from '@react-spring/web';
import { useEffect } from 'react';
import OptimizedImage from '../ui/OptimizedImage';
import { trackViewContent } from '../PixelTracker';

export default function InstructorSection() {
  const achievements = [
    { number: "1", text: "de 6 facilitadores autorizados en el mundo" },
    { number: "+800", text: "sesiones facilitadas" },
    { number: "15", text: "años transformando equipos de alto rendimiento" },
    { number: "100%", text: "de satisfacción en todas las ediciones" }
  ];

  const credentials = [
    "Trainer exclusivo Coaching x Valores",
    "Mentor acreditado por AMCES",
    "Coach ICF, ASESCO y AIDCP"
  ];

  // Move all hooks to top level
  const [headerRef, headerInView] = useInView({ once: true, rootMargin: '-80px' });
  const [statsRef, statsInView] = useInView({ once: true, rootMargin: '-80px' });
  const [credentialsRef, credentialsInView] = useInView({ once: true, rootMargin: '-80px' });
  const [leftRef, leftInView] = useInView({ once: true, rootMargin: '-80px' });
  const [rightRef, rightInView] = useInView({ once: true, rootMargin: '-80px' });
  const [ctaRef, ctaInView] = useInView({ once: true, rootMargin: '-80px' });

  const headerSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: headerInView ? 1 : 0 },
    config: { duration: 400 }
  });

  // Track when instructor section is viewed
  useEffect(() => {
    if (headerInView) {
      trackViewContent('Sección Instructor - Toño Galán');
    }
  }, [headerInView]);

  const leftSpring = useSpring({
    opacity: leftInView ? 1 : 0,
    transform: leftInView ? 'translateX(0px)' : 'translateX(-20px)',
    config: { duration: 400 }
  });

  const rightSpring = useSpring({
    opacity: rightInView ? 1 : 0,
    transform: rightInView ? 'translateX(0px)' : 'translateX(20px)',
    config: { duration: 400 },
    delay: 100
  });

  const ctaSpring = useSpring({
    opacity: ctaInView ? 1 : 0,
    transform: ctaInView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 },
    delay: 200
  });

  // Stats animations
  const statsSprings = useSprings(
    achievements.length,
    achievements.map((_, index) => ({
      opacity: statsInView ? 1 : 0,
      transform: statsInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Credentials animations
  const credentialsSprings = useSprings(
    credentials.length,
    credentials.map((_, index) => ({
      opacity: credentialsInView ? 1 : 0,
      transform: credentialsInView ? 'scale(1)' : 'scale(0.8)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  return (
    <section id="instructor" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Powerful headline */}
        <animated.div
          ref={headerRef}
          className="text-center mb-16"
          style={headerSpring}
        >
          <p className="text-lg text-[#97d700] font-bold uppercase tracking-wider mb-4">
            Tu mentor en esta transformación
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black">
            El facilitador marca
            <span className="block text-[#97d700]">la diferencia</span>
          </h2>
        </animated.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Image and stats column */}
          <animated.div
            ref={leftRef}
            className="order-2 lg:order-1"
            style={leftSpring}
          >
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-[#97d700]/20 rounded-full blur-3xl"></div>
              <div className="relative w-80 h-80 md:w-96 md:h-96 mx-auto rounded-full overflow-hidden shadow-2xl border-4 border-white">
                <OptimizedImage
                  src="/portrait.jpg"
                  alt="Toño Galán, fundador y facilitador principal de Coaching X Valores - Experto internacional en coaching con valores"
                  width={384}
                  height={384}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  sizes="(max-width: 768px) 320px, 384px"
                />
              </div>
            </div>

            {/* Stats grid */}
            <div ref={statsRef} className="grid grid-cols-2 gap-4 mb-8">
              {statsSprings.map((style, index) => {
                const achievement = achievements[index];
                return (
                  <animated.div
                    key={index}
                    style={style}
                    className="bg-white p-4 rounded-xl shadow-lg text-center"
                  >
                    <p className="text-3xl font-heading text-[#97d700] mb-1">{achievement.number}</p>
                    <p className="text-sm text-gray-600">{achievement.text}</p>
                  </animated.div>
                );
              })}
            </div>

            {/* Mi compromiso contigo - moved here for better balance */}
            <div className="bg-[#97d700] text-black p-6 rounded-2xl">
              <p className="text-xl font-heading uppercase tracking-wider mb-2">
                Mi compromiso contigo:
              </p>
              <p className="text-lg">
                No sales de aquí hasta que tengas la <span className="font-bold">claridad</span>, la <span className="font-bold">confianza</span> y las <span className="font-bold">herramientas</span> para brillar en cada sesión.
              </p>
            </div>
          </animated.div>

          {/* Content column */}
          <animated.div
            ref={rightRef}
            className="order-1 lg:order-2 space-y-6"
            style={rightSpring}
          >
            <div>
              <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-tight text-black mb-4">
                Soy Toño Galán
              </h3>
              <div ref={credentialsRef} className="flex flex-wrap gap-2 mb-6">
                {credentialsSprings.map((style, index) => {
                  const credential = credentials[index];
                  return (
                    <animated.span
                      key={index}
                      style={style}
                      className="px-4 py-2 bg-[#97d700]/20 text-black text-sm font-medium rounded-full"
                    >
                      {credential}
                    </animated.span>
                  );
                })}
              </div>
            </div>

            <div className="bg-black text-white p-6 rounded-2xl">
              <p className="text-xl font-heading uppercase tracking-wider mb-2">
                Mi misión es clara:
              </p>
              <p className="text-2xl text-[#97d700]">
                Que salgas de aquí siendo <span className="font-bold">EL coach</span> que tus clientes necesitan
              </p>
            </div>

            <div className="space-y-4 text-lg text-gray-700">
              <p>
                <span className="font-bold text-black">No soy un formador más.</span> Soy alguien que ha estado donde tú estás ahora. Que ha sentido la frustración de tener las herramientas pero no el método. La técnica pero no la profundidad.
              </p>

              <p>
                Desde 2019, he tenido el privilegio de certificar a más de <span className="font-bold text-[#97d700]">200 profesionales</span> que hoy están transformando vidas.
              </p>

              <p>
                Pero aquí está lo importante: <span className="font-bold text-black">no vengo solamente a enseñarte teoría</span>. Vengo a transmitirte una forma de ser y hacer coaching que cambiará para siempre tu práctica profesional.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl">
              <p className="text-xl italic text-gray-700 mb-4">
                &ldquo;Lo que hace único a este retiro no es solo la metodología. Es la forma en que la vivimos juntos. Es el proceso de transformación personal que atravesamos mientras aprendemos a transformar a otros.&rdquo;
              </p>
              <p className="text-right font-bold text-black">- Toño Galán</p>
            </div>

          </animated.div>
        </div>

        {/* CTA moved to bottom and centered */}
        <animated.div
          ref={ctaRef}
          className="mt-16 text-center"
          style={ctaSpring}
        >
          <a
            href="#registration"
            className="inline-block px-10 py-5 bg-black text-white font-heading uppercase tracking-wider font-bold text-xl rounded-full shadow-2xl hover:shadow-[#97d700]/30 transform hover:scale-105 transition-all duration-300"
          >
            QUIERO SER TU PRÓXIMO CASO DE ÉXITO
          </a>
        </animated.div>
      </div>
    </section>
  );
}
